import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import {
	DEFAULT_FILTER_OPERATORS,
	FILTER_OPERATOR_OPTIONS,
	getSiteConfig,
} from "./shopifyLiveSaleSiteConfig";
import "./shopifyGraphql.css";

const AUTO_REFRESH_MS = 60000;
const CHART_METRIC = "total_sales";
const CHART_LINE_COLOR = "#4db8e8";
const CHART_LINE_WIDTH = 1.75;
const CHART_LINE_SMOOTH = 0.5;
const CHART_PROJECTED_DASH = [2, 11];
const CHART_PROJECTED_DASH_SPEED = 22;

const TZ = "Asia/Kolkata";
const DATE_LABELS = {
	today: "Today",
	yesterday: "Yesterday",
	last_3_days: "Last 3 days",
	this_week: "This week",
	last_week: "Last week",
	last_7_days: "Last 7 days",
	last_30_days: "Last 30 days",
	this_month: "This month",
	last_month: "Last month",
};

const METRICS = {
	total_sales: { label: "Total sales", field: "total_sales", format: "money" },
	returns: { label: "Returns", field: "sales_reversals", format: "money" },
	orders: { label: "Orders", field: "orders", format: "number" },
	gross_sales: { label: "Gross sales", field: "gross_sales", format: "money" },
};

const HOURLY_DURING = new Set(["today", "yesterday", "last_3_days"]);
const DEFAULT_SOURCE = { label: "Live", color: "#008060", title: "Shopify GraphQL Admin API" };
const NUM = new Intl.NumberFormat("en-IN");

const mergeFilterOptionItems = (apiItems, fallbackValues = []) => {
	const seen = new Set();
	const merged = [];
	const addValue = (value) => {
		const normalized = String(value || "").trim();
		if (!normalized || seen.has(normalized)) return;
		seen.add(normalized);
		merged.push({ name: normalized, key: normalized });
	};
	fallbackValues.forEach(addValue);
	(apiItems || []).forEach((item) => addValue(item?.key || item?.name));
	return merged;
};

const buildFallbackFilterOptions = (siteConfig) => ({
	product_vendor: mergeFilterOptionItems([], siteConfig.defaultVendors),
	sales_channel: mergeFilterOptionItems([], siteConfig.fallbackSalesChannels),
	product_type: mergeFilterOptionItems([], siteConfig.defaultExcludedProductTypes),
});

const cloneDefaultFilters = (siteConfig) => ({
	product_vendor: [...siteConfig.defaultVendors],
	excluded_sales_channel: [...siteConfig.defaultExcludedSalesChannels],
	excluded_product_type: [...siteConfig.defaultExcludedProductTypes],
});

const cloneDefaultFilterOperators = () => ({ ...DEFAULT_FILTER_OPERATORS });

const pickDefaultsFromAvailable = (defaults, available = []) => {
	if (!available.length) return [...defaults];
	const picked = defaults.filter((value) => available.includes(value));
	return picked.length ? picked : [...defaults];
};

const resolveDefaultFiltersFromOptions = (filterOptionsPayload = {}, siteConfig) => {
	const availableVendors = (filterOptionsPayload.product_vendor || [])
		.map((item) => item?.key || item?.name)
		.filter(Boolean);
	const availableChannels = (filterOptionsPayload.sales_channel || [])
		.map((item) => item?.key || item?.name)
		.filter(Boolean);
	const availableTypes = (filterOptionsPayload.product_type || [])
		.map((item) => item?.key || item?.name)
		.filter(Boolean);

	return {
		product_vendor: pickDefaultsFromAvailable(siteConfig.defaultVendors, availableVendors),
		excluded_sales_channel: pickDefaultsFromAvailable(
			siteConfig.defaultExcludedSalesChannels,
			availableChannels
		),
		excluded_product_type: pickDefaultsFromAvailable(
			siteConfig.defaultExcludedProductTypes,
			availableTypes
		),
	};
};

const toISODate = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const defaultCustomDates = () => {
	const end = new Date();
	const start = new Date();
	start.setDate(start.getDate() - 2);
	return { start: toISODate(start), end: toISODate(end) };
};

const daysBetweenISO = (start, end) => {
	const startDate = new Date(`${start}T12:00:00`);
	const endDate = new Date(`${end}T12:00:00`);
	return Math.round((endDate - startDate) / 86400000) + 1;
};

const formatISORangeLabel = (start, end) => {
	const startDate = new Date(`${start}T12:00:00`);
	const endDate = new Date(`${end}T12:00:00`);
	const startYear = startDate.toLocaleDateString("en-IN", { timeZone: TZ, year: "numeric" });
	const startMonthDay = startDate.toLocaleDateString("en-IN", {
		timeZone: TZ,
		month: "short",
		day: "numeric",
	});
	const endMonthDay = endDate.toLocaleDateString("en-IN", {
		timeZone: TZ,
		month: "short",
		day: "numeric",
	});
	if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
		const month = startDate.toLocaleDateString("en-IN", { timeZone: TZ, month: "short" });
		return `${month} ${startDate.getDate()}-${endDate.getDate()}, ${startYear}`;
	}
	return `${startMonthDay}-${endMonthDay}, ${startYear}`;
};

const granularityFor = (during, meta = {}) => {
	if (meta.granularity) return meta.granularity;
	if (during === "custom" && meta.start && meta.end) {
		return daysBetweenISO(meta.start, meta.end) <= 3 ? "hour" : "day";
	}
	return HOURLY_DURING.has(during) ? "hour" : "day";
};

const periodColumnLabel = (granularity) => (granularity === "day" ? "Day" : "Hour");

const returnsOf = (row) => parseFloat(row?.sales_reversals ?? row?.returns ?? 0);

const buildCumulativeRows = (rows) => {
	let totalSales = 0;
	let returns = 0;
	let orders = 0;
	let grossSales = 0;
	return rows.map((row) => {
		totalSales += parseFloat(row?.total_sales ?? 0);
		returns += returnsOf(row);
		orders += parseInt(row?.orders ?? 0, 10);
		grossSales += parseFloat(row?.gross_sales ?? 0);
		return {
			...row,
			total_sales: totalSales,
			sales_reversals: returns,
			returns,
			orders,
			gross_sales: grossSales,
		};
	});
};

const rowsForDisplay = (rows, granularity) => (granularity === "day" ? buildCumulativeRows(rows) : rows);
const timeKey = (row) => row?.hour || row?.day;

const moneyFmt = (currency) =>
	new Intl.NumberFormat("en-IN", {
		style: "currency",
		currency,
		maximumFractionDigits: 2,
	});

const axisMoneyFmt = (currency) =>
	new Intl.NumberFormat("en-IN", {
		style: "currency",
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

const totalsOf = (row, key) => parseFloat(row?.[`${key}__totals`] ?? row?.[key] ?? 0);

const metricValue = (row, metricKey) => {
	if (metricKey === "returns") return returnsOf(row);
	return parseFloat(row?.[METRICS[metricKey].field] ?? 0);
};

const metricTotal = (row, metricKey) => {
	if (metricKey === "returns") return totalsOf(row, "sales_reversals") || totalsOf(row, "returns");
	return totalsOf(row, METRICS[metricKey].field);
};

const fmtDateLabel = (iso) =>
	new Date(iso).toLocaleDateString("en-IN", {
		timeZone: TZ,
		month: "short",
		day: "numeric",
		year: "numeric",
	});

const fmtDayShort = (iso) =>
	new Date(iso).toLocaleDateString("en-IN", {
		timeZone: TZ,
		month: "short",
		day: "numeric",
	});

const fmtTooltipTime = (iso) =>
	new Date(iso).toLocaleString("en-US", {
		timeZone: TZ,
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});

const fmtHourTick = (iso) =>
	new Date(iso).toLocaleString("en-IN", {
		timeZone: TZ,
		hour: "numeric",
		hour12: true,
	}).toLowerCase();
const fmtDateHourTick = (iso) =>
	`${new Date(iso).toLocaleDateString("en-IN", {
		timeZone: TZ,
		day: "numeric",
		month: "short",
	})} ${fmtHourTick(iso)}`;

const fmtChartTick = (iso, granularity) => (granularity === "day" ? fmtDayShort(iso) : fmtHourTick(iso));
const fmtDayRow = (iso) => fmtDateLabel(iso);
const fmtMonthShort = (iso) =>
	new Date(iso).toLocaleDateString("en-IN", {
		timeZone: TZ,
		month: "short",
	});
const dayOfMonth = (iso) =>
	parseInt(
		new Date(iso).toLocaleDateString("en-IN", {
			timeZone: TZ,
			day: "numeric",
		}),
		10
	);
const monthOfYear = (iso) =>
	parseInt(
		new Date(iso).toLocaleDateString("en-IN", {
			timeZone: TZ,
			month: "numeric",
		}),
		10
	);
const fmtTableDate = (iso) =>
	new Date(iso).toLocaleDateString("en-GB", {
		timeZone: TZ,
		day: "numeric",
		month: "short",
		year: "numeric",
	});
const fmtRefreshTime = (value) =>
	new Date(value).toLocaleTimeString("en-IN", {
		timeZone: TZ,
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});

const legendRangeLabel = (rows, during, granularity, meta = {}) => {
	if (during === "custom" && meta.start && meta.end) {
		return formatISORangeLabel(meta.start, meta.end);
	}
	if (!rows.length) return DATE_LABELS[during] || during;
	const start = new Date(timeKey(rows[0]));
	const end = new Date(timeKey(rows[rows.length - 1]));
	if (granularity === "day" && rows.length > 1) {
		const year = end.toLocaleDateString("en-IN", { timeZone: TZ, year: "numeric" });
		const startMonthDay = start.toLocaleDateString("en-IN", { timeZone: TZ, month: "short", day: "numeric" });
		const endMonthDay = end.toLocaleDateString("en-IN", { timeZone: TZ, month: "short", day: "numeric" });
		if (start.getMonth() === end.getMonth()) {
			const month = start.toLocaleDateString("en-IN", { timeZone: TZ, month: "short" });
			return `${month} ${start.getDate()}-${end.getDate()}, ${year}`;
		}
		return `${startMonthDay}-${endMonthDay}, ${year}`;
	}
	if (during === "today" || during === "yesterday") {
		return fmtDateLabel(timeKey(rows[0]));
	}
	return DATE_LABELS[during] || fmtDateLabel(timeKey(rows[0]));
};

const summaryPeriodLabel = (rows, during, granularity, meta = {}) => {
	if (during === "custom" && meta.start && meta.end) {
		if (daysBetweenISO(meta.start, meta.end) > 2) {
			return formatISORangeLabel(meta.start, meta.end);
		}
		if (granularity === "hour" && rows.length) {
			return fmtTableDate(timeKey(rows[0]));
		}
		return formatISORangeLabel(meta.start, meta.end);
	}
	if (during === "last_3_days" && rows.length) {
		const firstDate = new Date(timeKey(rows[0]));
		const lastDate = new Date(timeKey(rows[rows.length - 1]));
		const rangeStart = firstDate <= lastDate ? firstDate : lastDate;
		const rangeEnd = firstDate <= lastDate ? lastDate : firstDate;
		return formatISORangeLabel(toISODate(rangeStart), toISODate(rangeEnd));
	}
	if (granularity === "day" && rows.length > 1) {
		return legendRangeLabel(rows, during, granularity, meta);
	}
	if (rows.length && HOURLY_DURING.has(during)) {
		return fmtTableDate(timeKey(rows[0]));
	}
	return DATE_LABELS[during] || during;
};

const formatMetricValue = (value, metricKey, formatter) =>
	METRICS[metricKey].format === "number" ? NUM.format(value) : formatter.format(value);

const formatCurrencyValue = (value, formatter) => {
	const numericValue = Number(value || 0);
	const absValue = Math.abs(numericValue);
	const resolvedCurrency = formatter?.resolvedOptions?.()?.currency;
	const formatted =
		resolvedCurrency === "INR"
			? `₹${absValue.toLocaleString("en-IN", {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}`
			: formatter.format(absValue);
	return numericValue < 0 ? `-${formatted}` : formatted;
};

const compactListLabel = (values, fallback = "All") => {
	if (!Array.isArray(values) || !values.length) return fallback;
	if (values.length <= 2) return values.join(", ");
	return `${values[0]}, ${values[1]} +${values.length - 2}`;
};

const currencySymbol = (currency) => {
	if (currency === "INR") return "₹";
	const sample = moneyFmt(currency).format(0);
	return sample.replace(/[\d.,\s0]/g, "").trim() || currency;
};

const chartAxisLabel = (value, metricKey, currency) => {
	if (METRICS[metricKey].format === "number") return NUM.format(value);
	const symbol = currencySymbol(currency);
	const abs = Math.abs(value);
	if (abs >= 1000000) {
		const formatted = value / 1000000;
		return `${symbol}${Number.isInteger(formatted) ? formatted : formatted.toFixed(1)}M`;
	}
	if (abs >= 1000) {
		const formatted = value / 1000;
		return `${symbol}${Number.isInteger(formatted) ? formatted : formatted.toFixed(1)}K`;
	}
	if (Object.is(value, -0) || value === 0) {
		return `${symbol}0`;
	}
	return axisMoneyFmt(currency).format(value);
};

const daySpanForChartRows = (chartRows) => {
	if (!chartRows.length) return 1;
	const first = new Date(timeKey(chartRows[0].raw));
	const last = new Date(timeKey(chartRows[chartRows.length - 1].raw));
	return Math.max(1, Math.round(Math.abs(last - first) / 86400000) + 1);
};

const hourTickConfigFor = (daySpan) => {
	if (daySpan <= 1) return { step: 1, withDate: false };
	if (daySpan === 2) return { step: 3, withDate: true };
	if (daySpan === 3) return { step: 6, withDate: true };
	return { step: 8, withDate: true };
};

const dayTickConfigFor = (rowCount) => {
	if (rowCount <= 8) return { mode: "step", step: 1 };
	if (rowCount <= 15) return { mode: "step", step: 4 };
	if (rowCount <= 31) return { mode: "step", step: 7 };
	if (rowCount <= 62) return { mode: "step", step: 10 };
	if (rowCount <= 366) return { mode: "anchor-days" };
	return { mode: "anchor-months" };
};

const dayTickLabelFor = (iso, index, rowCount, config) => {
	const day = dayOfMonth(iso);
	if (index === 0 || index === rowCount - 1) return fmtDayShort(iso);
	if (config.mode === "step") {
		return index % config.step === 0 ? fmtDayShort(iso) : "";
	}
	if (config.mode === "anchor-days") {
		const shouldShow = day === 1 || day === 5 || day === 10 || day === 15 || day === 20 || day === 25;
		if (!shouldShow) return "";
		return day === 1 ? `1 ${fmtMonthShort(iso)}` : String(day);
	}
	return day === 1 || (day === 15 && monthOfYear(iso) % 3 === 0) ? fmtMonthShort(iso) : "";
};

const niceYScale = (values, metricKey) => {
	if (!values.length) return { min: 0, max: 10 };
	let min = Math.min(...values);
	let max = Math.max(...values);
	if (METRICS[metricKey].format !== "number" && metricKey !== "returns") {
		min = Math.min(0, min);
	}
	const span = Math.max(max - min, 1);
	let step = 1000;
	if (span >= 2000000) step = 1000000;
	else if (span >= 500000) step = 200000;
	else if (span >= 50000) step = 50000;
	else if (span >= 10000) step = 10000;
	const chartMin = min < 0 ? Math.floor(min / step) * step : 0;
	const chartMax = Math.ceil(max / step) * step || step;
	return { min: chartMin, max: chartMax };
};

const isZeroRow = (row) =>
	parseFloat(row?.total_sales ?? 0) === 0 &&
	returnsOf(row) === 0 &&
	parseInt(row?.orders ?? 0, 10) === 0 &&
	parseFloat(row?.gross_sales ?? 0) === 0;

const projectedStartIndex = (rows, metricKey) => {
	for (let index = rows.length - 1; index >= 0; index -= 1) {
		if (metricValue(rows[index], metricKey) !== 0) {
			return index + 1;
		}
	}
	return rows.length;
};

const parseCsv = (text) => {
	const lines = text
		.trim()
		.split(/\r?\n/)
		.filter((line) => line && line !== "end");
	if (!lines.length) return [];
	const headers = lines[0].split(",");
	return lines.slice(1).map((line) => {
		const cells = line.split(",");
		return headers.reduce((result, header, index) => {
			result[header] = cells[index];
			return result;
		}, {});
	});
};

const friendlyLiveError = (error) => {
	const message =
		error?.response?.data?.error || error?.response?.data?.message || error?.message || String(error || "Unknown error");
	if (message === "Failed to fetch" || message.includes("NetworkError") || message.includes("Load failed")) {
		return "Cannot reach the live API. Confirm the Flask API is running and this app points to it.";
	}
	return message;
};

const renderTooltipMarkerHtml = (isProjected) => {
	const base =
		"display:inline-block;width:8px;height:8px;margin-right:2px;border-radius:50%;flex-shrink:0;vertical-align:middle;";
	if (isProjected) {
		return `<span style="${base}border:1px solid ${CHART_LINE_COLOR};background:repeating-linear-gradient(135deg,${CHART_LINE_COLOR} 0 2px,#ffffff 2px 4px),#ffffff;" aria-hidden="true"></span>`;
	}
	return `<span style="${base}background:${CHART_LINE_COLOR};" aria-hidden="true"></span>`;
};

const CHART_SERIES_EMPHASIS = {
	focus: "series",
	scale: false,
	symbol: "circle",
	symbolSize: 10,
	itemStyle: {
		color: CHART_LINE_COLOR,
		borderColor: "#ffffff",
		borderWidth: 2,
	},
};

const PROJECTED_SYMBOL = `image://${encodeURIComponent(
	`<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
		<defs>
			<clipPath id="clip">
				<circle cx="8" cy="8" r="6"/>
			</clipPath>
		</defs>
		<circle cx="8" cy="8" r="6" fill="#ffffff" stroke="${CHART_LINE_COLOR}" stroke-width="2"/>
		<g clip-path="url(#clip)">
			<rect width="16" height="16" fill="#ffffff"/>
			<path d="M-2 12 L4 6 M2 16 L10 8 M8 16 L16 8 M12 18 L18 12" stroke="${CHART_LINE_COLOR}" stroke-width="2" stroke-linecap="round"/>
		</g>
	</svg>`
)}`;

const buildChartOption = ({ chartRows, metricKey, currency, granularity }) => {
	const formatter = moneyFmt(currency);
	const labels = chartRows.map((row) => fmtChartTick(timeKey(row.raw), granularity));
	const values = chartRows.map((row) => row.value);
	const yScale = niceYScale(values, metricKey);
	const daySpan = daySpanForChartRows(chartRows);
	const hourTickConfig = granularity === "hour" ? hourTickConfigFor(daySpan) : null;
	const dayTickConfig = granularity === "day" ? dayTickConfigFor(chartRows.length) : null;
	const splitAt = projectedStartIndex(chartRows.map((row) => row.raw), metricKey);
	const actualSeries = values.map((value, index) => (index < splitAt ? value : null));
	const projectedSeries = values.map((value, index) => {
		if (index < Math.max(splitAt - 1, 0)) return null;
		if (index === splitAt) {
			return {
				value,
				symbol: PROJECTED_SYMBOL,
				symbolSize: 12,
			};
		}
		if (index === splitAt - 1) {
			return {
				value,
				symbol: "none",
			};
		}
		return {
			value,
			symbol: "none",
		};
	});
	return {
		toolbox: { show: false },
		grid: { left: 64, right: 12, top: 4, bottom: 28 },
		tooltip: {
			trigger: "axis",
			className: "shopify-graphql-echarts-tooltip",
			backgroundColor: "#ffffff",
			borderColor: "#e3e3e3",
			borderWidth: 1,
			textStyle: { color: "#616161", fontSize: 12 },
			extraCssText:
				"box-shadow: 0 8px 24px -8px rgba(0,0,0,0.28), 0 3px 6px 0 rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06); border-radius: 12px;padding:0;",
			formatter: (params) => {
				const entries = Array.isArray(params) ? params : [params];
				const dataIndex = entries.find((entry) => entry?.dataIndex != null)?.dataIndex;
				if (dataIndex == null || !chartRows[dataIndex]) return "";
				const row = chartRows[dataIndex];
				const isProjected = dataIndex >= splitAt;
				return `
					<div class="shopify-graphql-tooltip">
						<div class="shopify-graphql-tooltip-title">${METRICS[metricKey].label}</div>
						<div class="shopify-graphql-tooltip-subtitle">
							${renderTooltipMarkerHtml(isProjected)}
							<span>${granularity === "day" ? fmtDayRow(timeKey(row.raw)) : fmtTooltipTime(timeKey(row.raw))}</span>
						</div>
						<div class="shopify-graphql-tooltip-value">
							${METRICS[metricKey].format === "number" ? formatMetricValue(row.value, metricKey, formatter) : formatCurrencyValue(row.value, formatter)}
						</div>
					</div>
				`;
			},
			padding: 0,
			transitionDuration: 0,
			axisPointer: {
				type: "line",
				snap: true,
				lineStyle: {
					color: "#cbcbcf",
					width: 1,
				},
				label: { show: false },
			},
		},
		xAxis: {
			type: "category",
			boundaryGap: false,
			data: labels,
			axisLine: { show: false },
			axisTick: { show: false },
			axisLabel: {
				color: "#70707b",
				fontSize: 11,
				interval: 0,
				formatter: (value, index) => {
					if (granularity === "hour") {
						const iso = timeKey(chartRows[index]?.raw);
						if (!iso) return "";
						const step = hourTickConfig?.step || 1;
						const isEdge = index === 0 || index === chartRows.length - 1;
						if (!isEdge && index % step !== 0) return "";
						return hourTickConfig?.withDate ? fmtDateHourTick(iso) : fmtHourTick(iso);
					}
					if (granularity === "day") {
						const iso = timeKey(chartRows[index]?.raw);
						if (!iso) return "";
						return dayTickLabelFor(iso, index, chartRows.length, dayTickConfig);
					}
					return value;
				},
			},
		},
		yAxis: {
			type: "value",
			min: yScale.min,
			max: yScale.max,
			splitLine: { lineStyle: { color: "#eeeeef" } },
			axisLine: { show: false },
			axisTick: { show: false },
			axisLabel: {
				color: "#70707b",
				fontSize: 11,
				margin: 10,
				formatter: (value) => chartAxisLabel(value, metricKey, currency),
			},
		},
		series: [
			{
				id: "actual",
				type: "line",
				data: actualSeries,
				smooth: CHART_LINE_SMOOTH,
				smoothMonotone: "x",
				showSymbol: false,
				lineStyle: {
					color: CHART_LINE_COLOR,
					width: CHART_LINE_WIDTH,
					cap: "round",
					join: "round",
				},
				itemStyle: { color: CHART_LINE_COLOR },
				emphasis: CHART_SERIES_EMPHASIS,
				connectNulls: false,
			},
			{
				id: "projected",
				type: "line",
				data: projectedSeries,
				smooth: CHART_LINE_SMOOTH,
				smoothMonotone: "x",
				showSymbol: false,
				animation: false,
				animationDurationUpdate: 0,
				lineStyle: {
					color: CHART_LINE_COLOR,
					width: CHART_LINE_WIDTH,
					type: CHART_PROJECTED_DASH,
					cap: "round",
					join: "round",
					opacity: 1,
					dashOffset: 0,
				},
				itemStyle: { color: CHART_LINE_COLOR },
				emphasis: CHART_SERIES_EMPHASIS,
				connectNulls: false,
			},
		],
	};
};

const ShopifyLiveSaleGraphQL = ({ siteName = "thor" }) => {
	const siteConfig = getSiteConfig(siteName);
	const initialCustomRange = defaultCustomDates();
	const [during, setDuring] = useState("today");
	const currency = "INR";
	const [customStart, setCustomStart] = useState(initialCustomRange.start);
	const [customEnd, setCustomEnd] = useState(initialCustomRange.end);
	const [rows, setRows] = useState([]);
	const [meta, setMeta] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [source, setSource] = useState(DEFAULT_SOURCE);
	const [lastRefreshedAt, setLastRefreshedAt] = useState(new Date());
	const [tableSort, setTableSort] = useState({ key: null, direction: "asc" });
	const [filterOptions, setFilterOptions] = useState(() => buildFallbackFilterOptions(siteConfig));
	const [filtersLoading, setFiltersLoading] = useState(false);
	const [filterOptionsError, setFilterOptionsError] = useState("");
	const [filters, setFilters] = useState(() => cloneDefaultFilters(siteConfig));
	const [filterOperators, setFilterOperators] = useState(cloneDefaultFilterOperators);
	const [vendorDraft, setVendorDraft] = useState(() => [...siteConfig.defaultVendors]);
	const [vendorSearch, setVendorSearch] = useState("");
	const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
	const [salesChannelDraft, setSalesChannelDraft] = useState(() => [
		...siteConfig.defaultExcludedSalesChannels,
	]);
	const [salesChannelSearch, setSalesChannelSearch] = useState("");
	const [salesChannelPickerOpen, setSalesChannelPickerOpen] = useState(false);
	const [operatorPickerOpen, setOperatorPickerOpen] = useState({
		product_vendor: false,
		sales_channel: false,
		product_type: false,
	});
	const [productTypeDraft, setProductTypeDraft] = useState(() => [
		...siteConfig.defaultExcludedProductTypes,
	]);
	const [productTypeSearch, setProductTypeSearch] = useState("");
	const [productTypePickerOpen, setProductTypePickerOpen] = useState(false);
	const filtersInitializedRef = useRef(false);
	const liveRequestSeqRef = useRef(0);

	const closeAllPickers = useCallback(() => {
		setVendorPickerOpen(false);
		setSalesChannelPickerOpen(false);
		setProductTypePickerOpen(false);
		setOperatorPickerOpen({
			product_vendor: false,
			sales_channel: false,
			product_type: false,
		});
		setVendorSearch("");
		setSalesChannelSearch("");
		setProductTypeSearch("");
	}, []);

	const anyPickerOpen =
		vendorPickerOpen ||
		salesChannelPickerOpen ||
		productTypePickerOpen ||
		operatorPickerOpen.product_vendor ||
		operatorPickerOpen.sales_channel ||
		operatorPickerOpen.product_type;

	useEffect(() => {
		if (!anyPickerOpen) return undefined;

		const handlePointerDown = (event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			if (
				target.closest(".shopify-graphql-pill-dropdown") ||
				target.closest(".shopify-graphql-operator-dropdown")
			) {
				return;
			}
			closeAllPickers();
		};

		document.addEventListener("mousedown", handlePointerDown);
		return () => document.removeEventListener("mousedown", handlePointerDown);
	}, [anyPickerOpen, closeAllPickers]);

	const applyDefaultFilterSelection = useCallback(
		(filterOptionsPayload = null) => {
			const defaultFilters = filterOptionsPayload
				? resolveDefaultFiltersFromOptions(filterOptionsPayload, siteConfig)
				: cloneDefaultFilters(siteConfig);
			const defaultOperators = cloneDefaultFilterOperators();
			setFilters(defaultFilters);
			setFilterOperators(defaultOperators);
			setVendorDraft([...defaultFilters.product_vendor]);
			setSalesChannelDraft([...defaultFilters.excluded_sales_channel]);
			setProductTypeDraft([...defaultFilters.excluded_product_type]);
		},
		[siteConfig]
	);

	const loadFilterOptions = useCallback(async () => {
		setFiltersLoading(true);
		setFilterOptionsError("");
		try {
			const response = await pythonVmInstance.get(`/reports/shopify_graphql_filter_options/${siteName}`, {
				timeout: 120000,
			});
			const payload = response?.data || {};
			const nextOptions = {
				product_vendor: mergeFilterOptionItems(payload?.product_vendor, siteConfig.defaultVendors),
				sales_channel: mergeFilterOptionItems(payload?.sales_channel, siteConfig.fallbackSalesChannels),
				product_type: mergeFilterOptionItems(
					payload?.product_type,
					siteConfig.defaultExcludedProductTypes
				),
			};
			setFilterOptions(nextOptions);
			if (payload?.warning) {
				setFilterOptionsError(String(payload.warning));
			}
			if (!filtersInitializedRef.current) {
				applyDefaultFilterSelection({
					product_vendor: nextOptions.product_vendor,
					sales_channel: nextOptions.sales_channel,
					product_type: nextOptions.product_type,
				});
				filtersInitializedRef.current = true;
			}
		} catch (optionsError) {
			const fallbackOptions = buildFallbackFilterOptions(siteConfig);
			setFilterOptions(fallbackOptions);
			if (!filtersInitializedRef.current) {
				applyDefaultFilterSelection({
					product_vendor: fallbackOptions.product_vendor,
					sales_channel: fallbackOptions.sales_channel,
					product_type: fallbackOptions.product_type,
				});
				filtersInitializedRef.current = true;
			}
			const message =
				optionsError?.response?.data?.message ||
				optionsError?.message ||
				"Failed to load filter options from Shopify.";
			setFilterOptionsError(message);
			console.warn("Failed to load Shopify GraphQL filters", optionsError);
		} finally {
			setFiltersLoading(false);
		}
	}, [applyDefaultFilterSelection, siteConfig, siteName]);

	const loadLive = useCallback(
		async (overrides = {}) => {
			const nextDuring = overrides.during ?? during;
			const nextStart = overrides.customStart ?? customStart;
			const nextEnd = overrides.customEnd ?? customEnd;

			if (nextDuring === "custom") {
				if (!nextStart || !nextEnd) {
					setError("Choose a start and end date.");
					setSource({ label: "Error", color: "#d72c0d", title: "Missing custom date range" });
					return;
				}
				if (nextEnd < nextStart) {
					setError("End date must be on or after start date.");
					setSource({ label: "Error", color: "#d72c0d", title: "Invalid custom date range" });
					return;
				}
			}

			const requestId = (liveRequestSeqRef.current += 1);
			const isStale = () => requestId !== liveRequestSeqRef.current;

			setLoading(true);
			setError("");
			setSource({ label: "Loading...", color: "#8a8a8a", title: "Fetching live data from Shopify" });

			try {
				const params = {
					during: nextDuring,
					currency,
					product_vendor: JSON.stringify(filters.product_vendor),
					excluded_sales_channel: JSON.stringify(filters.excluded_sales_channel),
					excluded_product_type: JSON.stringify(filters.excluded_product_type),
					product_vendor_operator: filterOperators.product_vendor,
					sales_channel_operator: filterOperators.sales_channel,
					product_type_operator: filterOperators.product_type,
				};
				if (nextDuring === "custom") {
					params.start = nextStart;
					params.end = nextEnd;
				}

				const response = await pythonVmInstance.get(`/reports/shopify_graphql_dashboard/${siteName}/api/report`, {
					params,
				});

				if (isStale()) return;

				const responseData = response?.data || {};
				const responseRows = responseData?.rows || [];
				if (!responseRows.length) {
					throw new Error("Live query returned no rows for this period.");
				}

				setRows(responseRows);
				setMeta(responseData);
				setSource(DEFAULT_SOURCE);
				setLastRefreshedAt(new Date());
			} catch (requestError) {
				if (isStale()) return;
				const message = friendlyLiveError(requestError);
				setError(message);
				setSource({ label: "Error", color: "#d72c0d", title: message });
				setLastRefreshedAt(new Date());
			} finally {
				if (!isStale()) {
					setLoading(false);
				}
			}
		},
		[currency, customEnd, customStart, during, filterOperators, filters, siteName]
	);

	useEffect(() => {
		filtersInitializedRef.current = false;
		liveRequestSeqRef.current += 1;
		setRows([]);
		setMeta({});
		setError("");
		setLoading(true);
		closeAllPickers();
		applyDefaultFilterSelection();
		loadFilterOptions();
	}, [applyDefaultFilterSelection, closeAllPickers, loadFilterOptions, siteConfig, siteName]);

	useEffect(() => {
		loadLive();
	}, [loadLive]);

	useEffect(() => {
		const timer = window.setInterval(() => {
			loadLive();
		}, AUTO_REFRESH_MS);
		return () => window.clearInterval(timer);
	}, [loadLive]);

	useEffect(() => {
		if (during !== "custom") return;
		loadLive({ during: "custom", customStart, customEnd });
	}, [customEnd, customStart, during, loadLive]);

	const activeDuring = meta?.during || during;
	const activeCurrency = (meta?.currency || currency || "INR").toUpperCase();
	const granularity = granularityFor(activeDuring, meta);
	const displayRows = useMemo(() => rowsForDisplay(rows, granularity), [rows, granularity]);
	const tableRows = useMemo(
		() =>
			displayRows.map((displayRow, index) => ({
				displayRow,
				rawRow: rows[index],
				originalIndex: index,
			})),
		[displayRows, rows]
	);
	const sortedTableRows = useMemo(() => {
		if (!tableSort.key) return tableRows;
		const directionFactor = tableSort.direction === "asc" ? 1 : -1;
		const readValue = (entry) => {
			switch (tableSort.key) {
				case "period":
					return new Date(timeKey(entry.rawRow)).getTime();
				case "total_sales":
					return parseFloat(entry.displayRow?.total_sales ?? 0);
				case "sales_reversals":
					return returnsOf(entry.displayRow);
				case "orders":
					return parseInt(entry.displayRow?.orders ?? 0, 10);
				case "gross_sales":
					return parseFloat(entry.displayRow?.gross_sales ?? 0);
				default:
					return entry.originalIndex;
			}
		};
		return [...tableRows].sort((left, right) => {
			const leftValue = readValue(left);
			const rightValue = readValue(right);
			if (leftValue === rightValue) {
				return left.originalIndex - right.originalIndex;
			}
			return (leftValue > rightValue ? 1 : -1) * directionFactor;
		});
	}, [tableRows, tableSort]);
	const formatter = useMemo(() => moneyFmt(activeCurrency), [activeCurrency]);

	const chartRows = useMemo(
		() =>
			displayRows.map((row, index) => ({
				raw: rows[index],
				value: metricValue(row, CHART_METRIC),
			})),
		[displayRows, rows]
	);

	const chartOption = useMemo(
		() => buildChartOption({ chartRows, metricKey: CHART_METRIC, currency: activeCurrency, granularity }),
		[activeCurrency, chartRows, granularity]
	);
	const chartRef = useRef(null);
	const hasProjectedSegment = useMemo(() => {
		if (!chartRows.length) return false;
		const splitAt = projectedStartIndex(
			chartRows.map((row) => row.raw),
			CHART_METRIC
		);
		return splitAt < chartRows.length;
	}, [chartRows]);

	useEffect(() => {
		if (!hasProjectedSegment) return undefined;

		let frameId = 0;
		let dashOffset = 0;
		let lastFrame = performance.now();

		const tick = (timestamp) => {
			const elapsed = Math.min(timestamp - lastFrame, 48) / 1000;
			lastFrame = timestamp;
			dashOffset -= CHART_PROJECTED_DASH_SPEED * elapsed;

			const chart = chartRef.current?.getEchartsInstance?.();
			if (chart && typeof chart.isDisposed === "function" && !chart.isDisposed()) {
				chart.setOption(
					{
						series: [
							{
								id: "projected",
								lineStyle: { dashOffset },
							},
						],
					},
					{ lazyUpdate: true }
				);
			}

			frameId = requestAnimationFrame(tick);
		};

		frameId = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frameId);
	}, [hasProjectedSegment, chartOption]);

	const legendLabel = rows.length ? legendRangeLabel(rows, activeDuring, granularity, meta) : DATE_LABELS[activeDuring];
	const heroValue = rows.length
		? METRICS[CHART_METRIC].format === "number"
			? formatMetricValue(metricTotal(rows[0], CHART_METRIC), CHART_METRIC, formatter)
			: formatCurrencyValue(metricTotal(rows[0], CHART_METRIC), formatter)
		: "-";
	const summaryLabel = rows.length ? summaryPeriodLabel(rows, activeDuring, granularity, meta) : "-";
	const activeDateLabel = during === "custom" ? formatISORangeLabel(customStart, customEnd) : DATE_LABELS[during] || during;
	const refreshedLabel = fmtRefreshTime(lastRefreshedAt);
	const siteLabel = siteConfig.storeLabel;

	const handleRefresh = () => {
		loadLive();
	};

	const handleDuringChange = (event) => {
		const nextDuring = event.target.value;
		setDuring(nextDuring);
		if (nextDuring === "custom") return;
		loadLive({ during: nextDuring });
	};

	const handleFilterOperatorChange = (filterKey, operator) => {
		setFilterOperators((previous) => ({
			...previous,
			[filterKey]: operator,
		}));
		closeAllPickers();
	};

	const toggleOperatorPicker = (filterKey, event) => {
		event.preventDefault();
		const willOpen = !operatorPickerOpen[filterKey];
		closeAllPickers();
		if (willOpen) {
			setOperatorPickerOpen({
				product_vendor: false,
				sales_channel: false,
				product_type: false,
				[filterKey]: true,
			});
		}
	};

	const operatorLabel = (operator) =>
		FILTER_OPERATOR_OPTIONS.find((option) => option.value === operator)?.label || "is one of";

	const renderOperatorPicker = (filterKey) => (
		<details
			className="shopify-graphql-pill-dropdown shopify-graphql-operator-dropdown"
			open={operatorPickerOpen[filterKey]}>
			<summary
				className="shopify-graphql-pill shopify-graphql-pill-summary"
				onClick={(event) => toggleOperatorPicker(filterKey, event)}>
				{operatorLabel(filterOperators[filterKey])}
			</summary>
			<div className="shopify-graphql-operator-menu">
				{FILTER_OPERATOR_OPTIONS.map((option) => (
					<button
						type="button"
						key={option.value}
						className={`shopify-graphql-operator-option${
							filterOperators[filterKey] === option.value ? " is-selected" : ""
						}`}
						onClick={() => handleFilterOperatorChange(filterKey, option.value)}>
						{option.label}
					</button>
				))}
			</div>
		</details>
	);

	const vendorOptions = useMemo(() => {
		const all = filterOptions.product_vendor.map((item) => item?.key || item?.name).filter(Boolean);
		const selectedSet = new Set(siteConfig.defaultVendors);
		const prioritized = siteConfig.defaultVendors.filter((vendor) => all.includes(vendor));
		const remainder = all.filter((vendor) => !selectedSet.has(vendor)).sort((a, b) => a.localeCompare(b));
		return [...prioritized, ...remainder];
	}, [filterOptions.product_vendor, siteConfig.defaultVendors]);
	const filteredVendorOptions = useMemo(() => {
		const query = vendorSearch.trim().toLowerCase();
		if (!query) return vendorOptions;
		return vendorOptions.filter((value) => value.toLowerCase().includes(query));
	}, [vendorOptions, vendorSearch]);
	const salesChannelOptions = useMemo(() => {
		const all = filterOptions.sales_channel.map((item) => item?.key || item?.name).filter(Boolean);
		const prioritized = all.includes(siteConfig.prioritizedSalesChannel)
			? [siteConfig.prioritizedSalesChannel]
			: [];
		const remainder = all
			.filter((channel) => channel !== siteConfig.prioritizedSalesChannel)
			.sort((a, b) => a.localeCompare(b));
		return [...prioritized, ...remainder];
	}, [filterOptions.sales_channel, siteConfig.prioritizedSalesChannel]);
	const filteredSalesChannelOptions = useMemo(() => {
		const query = salesChannelSearch.trim().toLowerCase();
		if (!query) return salesChannelOptions;
		return salesChannelOptions.filter((value) => value.toLowerCase().includes(query));
	}, [salesChannelOptions, salesChannelSearch]);
	const productTypeOptions = useMemo(() => {
		const all = filterOptions.product_type.map((item) => item?.key || item?.name).filter(Boolean);
		const defaultSet = new Set(siteConfig.defaultExcludedProductTypes);
		const prioritized = siteConfig.defaultExcludedProductTypes.filter((productType) =>
			all.includes(productType)
		);
		const remainder = all.filter((productType) => !defaultSet.has(productType)).sort((a, b) => a.localeCompare(b));
		return [...prioritized, ...remainder];
	}, [filterOptions.product_type, siteConfig.defaultExcludedProductTypes]);
	const filteredProductTypeOptions = useMemo(() => {
		const query = productTypeSearch.trim().toLowerCase();
		if (!query) return productTypeOptions;
		return productTypeOptions.filter((value) => value.toLowerCase().includes(query));
	}, [productTypeOptions, productTypeSearch]);

	const openSalesChannelPicker = () => {
		closeAllPickers();
		setSalesChannelDraft(filters.excluded_sales_channel);
		setSalesChannelSearch("");
		setSalesChannelPickerOpen(true);
	};

	const closeSalesChannelPicker = () => {
		setSalesChannelPickerOpen(false);
		setSalesChannelSearch("");
	};

	const toggleSalesChannelPicker = (event) => {
		event.preventDefault();
		if (salesChannelPickerOpen) {
			closeSalesChannelPicker();
		} else {
			openSalesChannelPicker();
		}
	};

	const toggleSalesChannelDraft = (value) => {
		if (value === "None") {
			setSalesChannelDraft([]);
			return;
		}
		setSalesChannelDraft((previous) => {
			const set = new Set(previous.filter((item) => item !== "None"));
			if (set.has(value)) {
				set.delete(value);
			} else {
				set.add(value);
			}
			return Array.from(set);
		});
	};

	const clearSalesChannelDraft = () => {
		setSalesChannelDraft([]);
	};

	const resetSalesChannelDraftToDefaults = () => {
		setSalesChannelDraft([...siteConfig.defaultExcludedSalesChannels]);
	};

	const openVendorPicker = () => {
		closeAllPickers();
		setVendorDraft(filters.product_vendor);
		setVendorSearch("");
		setVendorPickerOpen(true);
	};

	const closeVendorPicker = () => {
		setVendorPickerOpen(false);
		setVendorSearch("");
	};

	const toggleVendorPicker = (event) => {
		event.preventDefault();
		if (vendorPickerOpen) {
			closeVendorPicker();
		} else {
			openVendorPicker();
		}
	};

	const toggleVendorDraft = (value) => {
		if (value === "None") {
			setVendorDraft([]);
			return;
		}
		setVendorDraft((previous) => {
			const set = new Set(previous.filter((item) => item !== "None"));
			if (set.has(value)) {
				set.delete(value);
			} else {
				set.add(value);
			}
			return Array.from(set);
		});
	};

	const clearVendorDraft = () => {
		setVendorDraft([]);
	};

	const resetVendorDraftToDefaults = () => {
		setVendorDraft([...siteConfig.defaultVendors]);
	};

	const applyVendorDraft = () => {
		setFilters((previous) => ({
			...previous,
			product_vendor: [...vendorDraft],
		}));
		closeAllPickers();
	};

	const applySalesChannelDraft = () => {
		setFilters((previous) => ({
			...previous,
			excluded_sales_channel: [...salesChannelDraft],
		}));
		closeAllPickers();
	};

	const openProductTypePicker = () => {
		closeAllPickers();
		setProductTypeDraft(filters.excluded_product_type);
		setProductTypeSearch("");
		setProductTypePickerOpen(true);
	};

	const closeProductTypePicker = () => {
		setProductTypePickerOpen(false);
		setProductTypeSearch("");
	};

	const toggleProductTypePicker = (event) => {
		event.preventDefault();
		if (productTypePickerOpen) {
			closeProductTypePicker();
		} else {
			openProductTypePicker();
		}
	};

	const toggleProductTypeDraft = (value) => {
		if (value === "None") {
			setProductTypeDraft([]);
			return;
		}
		setProductTypeDraft((previous) => {
			const set = new Set(previous.filter((item) => item !== "None"));
			if (set.has(value)) {
				set.delete(value);
			} else {
				set.add(value);
			}
			return Array.from(set);
		});
	};

	const clearProductTypeDraft = () => {
		setProductTypeDraft([]);
	};

	const resetProductTypeDraftToDefaults = () => {
		setProductTypeDraft([...siteConfig.defaultExcludedProductTypes]);
	};

	const applyProductTypeDraft = () => {
		setFilters((previous) => ({
			...previous,
			excluded_product_type: [...productTypeDraft],
		}));
		closeAllPickers();
	};

	const restoreAllFilterDefaults = () => {
		applyDefaultFilterSelection({
			product_vendor: filterOptions.product_vendor,
			sales_channel: filterOptions.sales_channel,
			product_type: filterOptions.product_type,
		});
		closeAllPickers();
	};

	const handleTableSort = (key) => {
		setTableSort((previous) => {
			if (previous.key === key) {
				return {
					key,
					direction: previous.direction === "asc" ? "desc" : "asc",
				};
			}
			return { key, direction: "asc" };
		});
	};

	const sortIndicator = (key) => {
		if (tableSort.key !== key) return "↕";
		return tableSort.direction === "asc" ? "↑" : "↓";
	};

	const handleCsvUpload = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		const text = await file.text();
		const parsedRows = parseCsv(text);
		if (!parsedRows.length) {
			setError("Uploaded CSV did not contain any rows.");
			return;
		}
		setRows(parsedRows);
		setMeta({
			during,
			currency,
			granularity: parsedRows?.[0]?.day ? "day" : "hour",
			start: customStart,
			end: customEnd,
		});
		setError("");
		setLoading(false);
		setSource({ label: "CSV upload", color: "#8a8a8a", title: "Offline CSV fallback" });
		setLastRefreshedAt(new Date());
	};

	const todayIso = toISODate(new Date());

	return (
		<div className="shopify-graphql-scroll-root">
		<div className="shopify-graphql">
			<div className="shopify-graphql-page-header">
				<div className="shopify-graphql-page-header-row">
					<div className="shopify-graphql-page-header-main">
						<span className="shopify-graphql-page-refresh">{`Last refreshed: ${refreshedLabel}`}</span>
					</div>
					<span className="shopify-graphql-page-store">{siteLabel}</span>
				</div>
			</div>
			<div className="shopify-graphql-topbar">
				<div className="shopify-graphql-controls">
					<button
						type="button"
						className="shopify-graphql-icon-button"
						onClick={handleRefresh}
						title="Refresh now (auto-refreshes every minute)">
						<RefreshRoundedIcon sx={{ fontSize: 18 }} />
					</button>
					<button
						type="button"
						className="shopify-graphql-pill shopify-graphql-pill-primary"
						onClick={restoreAllFilterDefaults}
						title="Restore default filter selections">
						Default selection
					</button>
					<select className="shopify-graphql-pill shopify-graphql-select" value={during} onChange={handleDuringChange}>
						<option value="today">Today</option>
						<option value="yesterday">Yesterday</option>
						<option value="last_3_days">Last 3 days</option>
						<option value="this_week">This week</option>
						<option value="last_week">Last week</option>
						<option value="last_7_days">Last 7 days</option>
						<option value="last_30_days">Last 30 days</option>
						<option value="this_month">This month</option>
						<option value="last_month">Last month</option>
						<option value="custom">Custom range...</option>
					</select>
					{during === "custom" && (
						<div className="shopify-graphql-custom-range shopify-graphql-custom-range-inline">
							<input type="date" value={customStart} max={todayIso} onChange={(event) => setCustomStart(event.target.value)} />
							<span className="shopify-graphql-range-separator">-</span>
							<input type="date" value={customEnd} max={todayIso} onChange={(event) => setCustomEnd(event.target.value)} />
						</div>
					)}

					<div className="shopify-graphql-filter-inline">
						<span className="shopify-graphql-filter-inline-label">Product vendor</span>
						{renderOperatorPicker("product_vendor")}
						<details className="shopify-graphql-pill-dropdown" open={vendorPickerOpen}>
							<summary className="shopify-graphql-pill shopify-graphql-pill-summary" onClick={toggleVendorPicker}>
								{compactListLabel(filters.product_vendor, "All vendors")}
							</summary>
							<div className="shopify-graphql-filter-menu shopify-graphql-filter-menu-vendor">
								<input
									type="search"
									className="shopify-graphql-filter-search"
									placeholder="Search"
									value={vendorSearch}
									onChange={(event) => setVendorSearch(event.target.value)}
								/>
								<div className="shopify-graphql-filter-options">
									{filteredVendorOptions.map((value) => (
										<label key={value}>
											<input
												type="checkbox"
												checked={
													value === "None"
														? vendorDraft.length === 0
														: vendorDraft.includes(value)
												}
												onChange={() => toggleVendorDraft(value)}
											/>
											<span>{value}</span>
										</label>
									))}
								</div>
								<div className="shopify-graphql-filter-actions">
									<button type="button" className="shopify-graphql-filter-clear" onClick={clearVendorDraft}>
										Clear
									</button>
									<button
										type="button"
										className="shopify-graphql-filter-defaults"
										onClick={resetVendorDraftToDefaults}>
										Defaults
									</button>
									<button type="button" className="shopify-graphql-filter-apply" onClick={applyVendorDraft}>
										Apply
									</button>
								</div>
							</div>
						</details>
					</div>

					<div className="shopify-graphql-filter-inline">
						<span className="shopify-graphql-filter-inline-label">Sales channel</span>
						{renderOperatorPicker("sales_channel")}
						<details className="shopify-graphql-pill-dropdown" open={salesChannelPickerOpen}>
							<summary
								className="shopify-graphql-pill shopify-graphql-pill-summary"
								onClick={toggleSalesChannelPicker}>
								{compactListLabel(filters.excluded_sales_channel, "None")}
							</summary>
							<div className="shopify-graphql-filter-menu shopify-graphql-filter-menu-vendor">
								<input
									type="search"
									className="shopify-graphql-filter-search"
									placeholder="Search"
									value={salesChannelSearch}
									onChange={(event) => setSalesChannelSearch(event.target.value)}
								/>
								<div className="shopify-graphql-filter-options">
									{filteredSalesChannelOptions.map((value) => (
										<label key={value}>
											<input
												type="checkbox"
												checked={
													value === "None"
														? salesChannelDraft.length === 0
														: salesChannelDraft.includes(value)
												}
												onChange={() => toggleSalesChannelDraft(value)}
											/>
											<span>{value}</span>
										</label>
									))}
								</div>
								<div className="shopify-graphql-filter-actions">
									<button type="button" className="shopify-graphql-filter-clear" onClick={clearSalesChannelDraft}>
										Clear
									</button>
									<button
										type="button"
										className="shopify-graphql-filter-defaults"
										onClick={resetSalesChannelDraftToDefaults}>
										Defaults
									</button>
									<button type="button" className="shopify-graphql-filter-apply" onClick={applySalesChannelDraft}>
										Apply
									</button>
								</div>
							</div>
						</details>
					</div>

					<div className="shopify-graphql-filter-inline">
						<span className="shopify-graphql-filter-inline-label">Product type</span>
						{renderOperatorPicker("product_type")}
						<details className="shopify-graphql-pill-dropdown" open={productTypePickerOpen}>
							<summary className="shopify-graphql-pill shopify-graphql-pill-summary" onClick={toggleProductTypePicker}>
								{compactListLabel(filters.excluded_product_type, "None")}
							</summary>
							<div className="shopify-graphql-filter-menu shopify-graphql-filter-menu-vendor">
								<input
									type="search"
									className="shopify-graphql-filter-search"
									placeholder="Search"
									value={productTypeSearch}
									onChange={(event) => setProductTypeSearch(event.target.value)}
								/>
								<div className="shopify-graphql-filter-options">
									{filteredProductTypeOptions.map((value) => (
										<label key={value}>
											<input
												type="checkbox"
												checked={
													value === "None"
														? productTypeDraft.length === 0
														: productTypeDraft.includes(value)
												}
												onChange={() => toggleProductTypeDraft(value)}
											/>
											<span>{value}</span>
										</label>
									))}
								</div>
								<div className="shopify-graphql-filter-actions">
									<button type="button" className="shopify-graphql-filter-clear" onClick={clearProductTypeDraft}>
										Clear
									</button>
									<button
										type="button"
										className="shopify-graphql-filter-defaults"
										onClick={resetProductTypeDraftToDefaults}>
										Defaults
									</button>
									<button type="button" className="shopify-graphql-filter-apply" onClick={applyProductTypeDraft}>
										Apply
									</button>
								</div>
							</div>
						</details>
					</div>

					{filtersLoading ? <span className="shopify-graphql-filters-loading">Filter lists loading...</span> : null}
					{filterOptionsError ? (
						<span className="shopify-graphql-filters-warning" title={filterOptionsError}>
							Default filter lists
						</span>
					) : null}
				</div>

				<div className="shopify-graphql-meta">
					<span className="shopify-graphql-source" style={{ color: source.color }} title={source.title}>
						{`● ${source.label}`}
					</span>
					<span className="shopify-graphql-range-label">{activeDateLabel}</span>
				</div>
			</div>

			<div className="shopify-graphql-container">
				{loading && !rows.length ? (
					<div className="shopify-graphql-empty">Loading from Shopify...</div>
				) : error && !rows.length ? (
					<div className="shopify-graphql-empty">
						<strong>Could not load live Shopify data</strong>
						<p className="shopify-graphql-error-detail">{error}</p>
						<p className="shopify-graphql-error-hint">
							All date ranges require the Flask API and Shopify credentials.
						</p>
						<div className="shopify-graphql-error-actions">
							<button type="button" className="shopify-graphql-pill shopify-graphql-pill-apply" onClick={handleRefresh}>
								Retry
							</button>
							<label className="shopify-graphql-pill shopify-graphql-upload">
								Upload CSV
								<input type="file" accept=".csv" onChange={handleCsvUpload} />
							</label>
						</div>
					</div>
				) : !rows.length ? (
					<div className="shopify-graphql-empty">No data for this period.</div>
				) : (
					<div className={`shopify-graphql-card ${loading ? "shopify-graphql-card-loading" : ""}`.trim()}>
						<section className="shopify-graphql-viz">
							<div className="shopify-graphql-hero">
								<div className="shopify-graphql-hero-label">{METRICS[CHART_METRIC].label}</div>
								<div className="shopify-graphql-hero-value">{heroValue}</div>
							</div>
							<div className="shopify-graphql-chart-wrap">
								<ReactECharts
									ref={chartRef}
									option={chartOption}
									style={{ height: 330, width: "100%" }}
									notMerge
									lazyUpdate
								/>
							</div>
							<div className="shopify-graphql-legend">
								<span className="shopify-graphql-legend-dot" />
								<span>{legendLabel}</span>
							</div>
						</section>

						<section className="shopify-graphql-table-section">
							<div className="shopify-graphql-table-scroll">
								<table className="shopify-graphql-table">
									<colgroup>
										<col className="shopify-graphql-col-period" />
										<col />
										<col />
										<col />
										<col />
									</colgroup>
									<thead>
										<tr>
											<th>
												<button
													type="button"
													className={`shopify-graphql-sort-btn ${tableSort.key === "period" ? "is-active" : ""}`.trim()}
													onClick={() => handleTableSort("period")}>
													<span>{periodColumnLabel(granularity)}</span>
													<span className="shopify-graphql-sort-indicator">{sortIndicator("period")}</span>
												</button>
											</th>
											<th>
												<button
													type="button"
													className={`shopify-graphql-sort-btn ${tableSort.key === "total_sales" ? "is-active" : ""}`.trim()}
													onClick={() => handleTableSort("total_sales")}>
													<span>Total sales</span>
													<span className="shopify-graphql-sort-indicator">{sortIndicator("total_sales")}</span>
												</button>
											</th>
											<th>
												<button
													type="button"
													className={`shopify-graphql-sort-btn ${tableSort.key === "sales_reversals" ? "is-active" : ""}`.trim()}
													onClick={() => handleTableSort("sales_reversals")}>
													<span>Sales reversals</span>
													<span className="shopify-graphql-sort-indicator">{sortIndicator("sales_reversals")}</span>
												</button>
											</th>
											<th>
												<button
													type="button"
													className={`shopify-graphql-sort-btn ${tableSort.key === "orders" ? "is-active" : ""}`.trim()}
													onClick={() => handleTableSort("orders")}>
													<span>Orders</span>
													<span className="shopify-graphql-sort-indicator">{sortIndicator("orders")}</span>
												</button>
											</th>
											<th>
												<button
													type="button"
													className={`shopify-graphql-sort-btn ${tableSort.key === "gross_sales" ? "is-active" : ""}`.trim()}
													onClick={() => handleTableSort("gross_sales")}>
													<span>Gross sales</span>
													<span className="shopify-graphql-sort-indicator">{sortIndicator("gross_sales")}</span>
												</button>
											</th>
										</tr>
									</thead>
									<tbody>
										<tr className="shopify-graphql-summary-row">
											<td>{summaryLabel}</td>
											<td>{formatCurrencyValue(totalsOf(rows[0], "total_sales"), formatter)}</td>
											<td>{formatCurrencyValue(totalsOf(rows[0], "sales_reversals") || totalsOf(rows[0], "returns"), formatter)}</td>
											<td>{NUM.format(parseInt(rows[0]?.orders__totals ?? rows[0]?.orders ?? 0, 10))}</td>
											<td>{formatCurrencyValue(totalsOf(rows[0], "gross_sales"), formatter)}</td>
										</tr>
										{sortedTableRows.map((entry) => (
											<tr
												key={`${timeKey(entry.rawRow)}-${entry.originalIndex}`}
												className={isZeroRow(entry.displayRow) ? "shopify-graphql-zero-row" : ""}>
												<td>{granularity === "day" ? fmtTableDate(timeKey(entry.rawRow)) : fmtTooltipTime(timeKey(entry.rawRow))}</td>
												<td>{formatCurrencyValue(parseFloat(entry.displayRow?.total_sales ?? 0), formatter)}</td>
												<td>{formatCurrencyValue(returnsOf(entry.displayRow), formatter)}</td>
												<td>{NUM.format(parseInt(entry.displayRow?.orders ?? 0, 10))}</td>
												<td>{formatCurrencyValue(parseFloat(entry.displayRow?.gross_sales ?? 0), formatter)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
						{loading && (
							<div className="shopify-graphql-loading-overlay" role="status" aria-live="polite" aria-label="Updating chart data">
								<span className="shopify-graphql-loading-spinner" />
							</div>
						)}
					</div>
				)}
			</div>
		</div>
		</div>
	);
};

export default ShopifyLiveSaleGraphQL;
